import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Monitor,
  Server,
  Shield,
  Zap,
  Database,
  Terminal,
  Cloud,
  Users,
  ChevronDown,
  ChevronRight,
  Play,
  CheckCircle2,
  Clock,
  ArrowRight,
} from "lucide-react";

const curriculum = [
  {
    title: "Installation & Initial Configuration",
    items: [
      "Windows Server editions & roles",
      "Storage layout, disks, and volumes",
      "Initial server configuration (sconfig)",
      "Joining domain vs workgroup",
    ],
  },
  {
    title: "Active Directory Domain Services (AD DS)",
    items: [
      "Promote Domain Controller (DC) & DSRM",
      "User, group, OU design & delegation",
      "Group Policy Objects (GPO) basics",
      "Replication & Sites",
    ],
  },
  {
    title: "Networking: DNS, DHCP & IPAM",
    items: [
      "DNS zones, records, forwarders",
      "DHCP scopes, reservations, policies",
      "IPAM overview and management",
      "Name resolution troubleshooting",
    ],
  },
  {
    title: "Group Policy & Security",
    items: [
      "GPO creation & link/order/enforcement",
      "Security baselines & LAPS",
      "NTFS share perms vs share perms",
      "Account policies & authentication",
    ],
  },
  {
    title: "Virtualization & Hyper-V",
    items: [
      "Hyper-V role & VM lifecycle",
      "Virtual switches, checkpoints, replication",
      "Resource control & storage options",
    ],
  },
  {
    title: "Automation & PowerShell",
    items: [
      "PowerShell core concepts & modules",
      "Remoting, scripts, scheduled tasks",
      "Desired State Configuration (DSC)",
    ],
  },
  {
    title: "Updates, Backup & HA",
    items: [
      "WSUS basics & patch management",
      "Windows Server Backup, VSS",
      "Failover Clustering fundamentals",
    ],
  },
];
const labs = [
  {
    title: "Lab 1 — Server Install & Join Domain",
    desc: "Install Server, configure roles, join AD.",
  },
  {
    title: "Lab 2 — AD & GPO",
    desc: "Create OUs, users, GPOs and apply policies.",
  },
  {
    title: "Lab 3 — DNS/DHCP Setup",
    desc: "Create DNS zones and DHCP scopes.",
  },
  {
    title: "Lab 4 — Hyper-V & VM Management",
    desc: "Create VMs, virtual switches and snapshots.",
  },
  {
    title: "Lab 5 — PowerShell Automation",
    desc: "Write scripts for user provisioning & reports.",
  },
];
const skills = [
  { icon: <Monitor className="h-6 w-6" />, label: "Server OS" },
  { icon: <Users className="h-6 w-6" />, label: "Active Directory" },
  { icon: <Shield className="h-6 w-6" />, label: "Security & GPOs" },
  { icon: <Database className="h-6 w-6" />, label: "DNS & DHCP" },
  { icon: <Cloud className="h-6 w-6" />, label: "Azure Hybrid" },
  { icon: <Terminal className="h-6 w-6" />, label: "PowerShell" },
  { icon: <Server className="h-6 w-6" />, label: "Hyper-V" },
  { icon: <Zap className="h-6 w-6" />, label: "Automation" },
];
const careers = [
  {
    title: "Windows System Administrator",
    note: "Manage servers, AD, and core infra.",
  },
  {
    title: "Infrastructure Engineer",
    note: "Design & operate enterprise systems.",
  },
  {
    title: "Cloud/Hybrid Admin",
    note: "Integrate on-prem with Azure AD & services.",
  },
  {
    title: "IT Security Administrator",
    note: "Harden servers & manage security policies.",
  },
];

export default function WindowsServerAdmin() {
  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 80 });
    window.scrollTo(0, 0);
  }, []);

  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-hidden">
      {/* HERO */}
      <section className="bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 py-12 mt-5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid gap-6 md:grid-cols-3 items-stretch">
            {/* Left Content */}
            <div
              className="md:col-span-2 flex flex-col justify-center"
              data-aos="fade-right"
            >
              <h1 className="text-3xl sm:text-4xl font-extrabold text-blue-800 animate-bounce">
                Microsoft Windows Server Administration
              </h1>
              <p className="mt-3 text-slate-600 max-w-2xl animate-fade-in">
                Learn to install, configure, secure and manage Windows Server
                for enterprise environments. Practical labs, PowerShell
                automation, AD, virtualization and high availability.
              </p>

              <div className="mt-6 flex gap-3 flex-wrap">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded shadow animate-pulse"
                >
                  <Play className="h-4 w-4 animate-bounce" /> Enroll Now
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 border border-slate-200 px-4 py-2 rounded hover:bg-slate-50 animate-wiggle"
                >
                  <ArrowRight className="h-4 w-4 animate-bounce" /> Apply
                </Link>
              </div>
            </div>

            {/* Right Tiles */}
            <div className="space-y-4" data-aos="fade-left">
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    icon: (
                      <Server className="h-6 w-6 text-blue-700 animate-bounce" />
                    ),
                    title: "Role-Based Learning",
                    subtitle: "Admin → Engineer",
                  },
                  {
                    icon: (
                      <Shield className="h-6 w-6 text-amber-600 animate-pulse" />
                    ),
                    title: "Security Focus",
                    subtitle: "GPOs & hardening",
                  },
                  {
                    icon: (
                      <Terminal className="h-6 w-6 text-sky-600 animate-wiggle" />
                    ),
                    title: "PowerShell Driven",
                    subtitle: "Automation & scripts",
                  },
                  {
                    icon: (
                      <Cloud className="h-6 w-6 text-cyan-600 animate-float" />
                    ),
                    title: "Hybrid Ready",
                    subtitle: "Azure integrations",
                  },
                ].map((tile, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-slate-200 p-4 bg-blue-50/20 shadow-sm hover:shadow-md transition"
                  >
                    <div className="flex items-center gap-3">
                      {tile.icon}
                      <div>
                        <div className="text-sm font-semibold">
                          {tile.title}
                        </div>
                        <div className="text-xs text-slate-500">
                          {tile.subtitle}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Dashboard */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2
          className="text-xl font-bold text-slate-800 mb-4"
          data-aos="fade-right"
        >
          Skills You'll Gain
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-4">
          {skills.map((s, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-slate-200 p-3 flex flex-col items-center gap-2 bg-blue-50/20 shadow-sm hover:scale-105 transition"
              data-aos="zoom-in"
              data-aos-delay={idx * 80}
            >
              <div className="p-2 rounded bg-blue-50 animate-bounce">
                {s.icon}
              </div>
              <div className="text-sm text-slate-700 animate-float">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Curriculum Accordion */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-4" data-aos="zoom-in">
          Curriculum
        </h2>
        <div className="space-y-3">
          {curriculum.map((mod, i) => {
            const open = openIndex === i;
            return (
              <div
                key={i}
                className="border rounded-lg overflow-hidden bg-blue-50/20 shadow-sm"
                data-aos="fade-up"
                data-aos-delay={i * 80}
              >
                <button
                  onClick={() => setOpenIndex(open ? null : i)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50 transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-sm font-semibold">
                      {i + 1}
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-slate-800">
                        {mod.title}
                      </div>
                      <div className="text-xs text-slate-500">
                        Modules: {mod.items.length}
                      </div>
                    </div>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-slate-600 transform ${
                      open ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`px-6 pb-4 transition-all duration-300 ${
                    open ? "block" : "hidden"
                  } bg-blue-50/10`}
                >
                  <ul className="mt-3 space-y-2">
                    {mod.items.map((it, k) => (
                      <li key={k} className="flex items-start gap-3">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 animate-bounce mt-1" />
                        <div className="text-sm text-slate-700 animate-float">
                          {it}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Labs */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-4" data-aos="fade-up-left">
          Hands-on Labs (Step-by-step)
        </h2>
        <div className="space-y-4">
          {labs.map((l, i) => (
            <div
              key={i}
              className="flex items-start gap-4 border rounded-lg p-4 bg-blue-50/20 shadow-sm hover:shadow-md transition"
              data-aos="fade-right"
              data-aos-delay={i * 100}
            >
              <div className="flex flex-col items-center">
                <div className="h-8 w-8 rounded-full bg-blue-700 text-white grid place-items-center font-semibold animate-bounce">
                  {i + 1}
                </div>
                {i !== labs.length - 1 && (
                  <div
                    className="h-full w-px bg-blue-200 mt-2"
                    style={{ height: 40 }}
                  />
                )}
              </div>
              <div>
                <div className="font-semibold text-slate-800 animate-wiggle">
                  {l.title}
                </div>
                <div className="text-sm text-slate-600 mt-1 animate-float">
                  {l.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-4" data-aos="fade-up-right">
          Career Roadmap
        </h2>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            {
              title: "Junior Admin",
              note: "Assist with server ops & AD",
            },
            {
              title: "System Administrator",
              note: "Manage servers & services",
            },
            {
              title: "Infrastructure Engineer",
              note: "Design & optimize infra",
            },
            {
              title: "Cloud/Hybrid Architect",
              note: "Lead hybrid cloud initiatives",
            },
          ].map((m, idx) => (
            <div
              key={idx}
              className="rounded-lg border p-4 bg-blue-50/20 shadow-sm hover:scale-102 transition"
              data-aos="zoom-in"
              data-aos-delay={idx * 80}
            >
              <div className="text-sm text-slate-500">Step {idx + 1}</div>
              <div className="font-semibold mt-2 animate-bounce">{m.title}</div>
              <div className="text-sm text-slate-600 mt-1 animate-float">
                {m.note}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Careers */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-4" data-aos="flip-up">
          Roles You Can Apply For
        </h2>
        <div className="flex gap-4 overflow-x-auto pb-3 no-scrollbar">
          {careers.map((c, i) => (
            <div
              key={i}
              className="min-w-[260px] rounded-lg border p-4 bg-blue-50/20 shadow-sm hover:scale-105 transition"
              data-aos="fade-left"
              data-aos-delay={i * 100}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-800 animate-wiggle">
                    {c.title}
                  </div>
                  <div className="text-sm text-slate-600 mt-1 animate-float">
                    {c.note}
                  </div>
                </div>
                <div className="text-blue-700 animate-bounce">
                  <ChevronRight className="h-5 w-5" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-4" data-aos="zoom-out">
          Frequently Asked
        </h2>
        <div className="space-y-4">
          {[
            {
              q: "Do I need prior Windows Server experience?",
              a: "Basic familiarity with operating systems helps, but we start from foundations and build up.",
            },
            {
              q: "Will I learn PowerShell?",
              a: "Yes — we include hands-on PowerShell modules and automation labs.",
            },
            {
              q: "Is this course job-oriented?",
              a: "Absolutely — labs, real scenarios and interview prep are part of the program.",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="flex items-start gap-4"
              data-aos="fade-up"
              data-aos-delay={i * 120}
            >
              <div className="min-w-[40px]">
                <div className="h-10 w-10 rounded-full bg-blue-700 text-white grid place-items-center">
                  IT
                </div>
              </div>
              <div className="flex-1">
                <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                  <div className="font-medium text-slate-800">{f.q}</div>
                </div>
                <div className="mt-2 bg-white border border-slate-100 rounded-lg p-3 ml-6">
                  <div className="text-sm text-slate-700">{f.a}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid gap-6 md:grid-cols-2 items-stretch">
          {/* Bigger first CTA */}
          <div
            className="rounded-xl border border-slate-200 p-10 bg-gradient-to-r from-blue-700 to-blue-600 text-white shadow-lg hover:scale-105 transition transform-gpu"
            data-aos="flip-left"
          >
            <div className="text-2xl font-bold">
              Get Certified in Windows Server
            </div>
            <p className="mt-3 max-w-md text-blue-100">
              Build your career with enterprise-grade skills in Active
              Directory, Hyper-V, GPOs and automation.
            </p>
            <Link
              to="/contact"
              className="inline-block mt-5 bg-white text-blue-700 px-5 py-2 rounded shadow font-medium hover:bg-blue-50"
            >
              Enroll Today
            </Link>
          </div>

          {/* Bigger second CTA */}
          <div
            className="rounded-xl border border-slate-200 p-10 bg-gradient-to-r from-slate-50 to-slate-100 text-slate-800 shadow-lg hover:scale-105 transition transform-gpu"
            data-aos="flip-right"
          >
            <div className="text-2xl font-bold">Still Thinking?</div>
            <p className="mt-3 max-w-md text-slate-600">
              Schedule a call with our mentors to learn how this course fits
              your career goals.
            </p>
            <Link
              to="/contact"
              className="inline-block mt-5 bg-blue-700 text-white px-5 py-2 rounded shadow font-medium hover:bg-blue-800"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
